/**
 * eBay: connect the seller account, publish items, and keep stock in step.
 *
 * The OAuth consent flow opens on eBay and redirects back with a code, which
 * is pasted here. The tokens themselves never reach the browser — the backend
 * exchanges and stores them.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import * as api from '../lib/api';
import { useInventory } from '../lib/useInventory';

/** eBay condition enums, ordered for a second-hand games seller. */
const CONDITIONS = [
  ['USED_EXCELLENT', 'Used - Excellent'],
  ['USED_VERY_GOOD', 'Used - Very Good'],
  ['USED_GOOD', 'Used - Good'],
  ['USED_ACCEPTABLE', 'Used - Acceptable'],
  ['NEW', 'New'],
  ['LIKE_NEW', 'Like New'],
  ['NEW_OTHER', 'New (other)'],
  ['FOR_PARTS_OR_NOT_WORKING', 'For parts / not working'],
];

export default function EbayPage() {
  const { db, setDb, databases, items } = useInventory();
  const [status, setStatus] = useState<Awaited<ReturnType<typeof api.ebayStatus>> | null>(
    null,
  );
  const [code, setCode] = useState('');
  const [links, setLinks] = useState<Record<string, Record<string, unknown>>>({});
  const [policies, setPolicies] = useState<Awaited<
    ReturnType<typeof api.ebayPolicies>
  > | null>(null);

  const [selected, setSelected] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [condition, setCondition] = useState(CONDITIONS[0][0]);
  const [location, setLocation] = useState('');
  const [fulfillment, setFulfillment] = useState('');
  const [paymentPolicy, setPaymentPolicy] = useState('');
  const [returnPolicy, setReturnPolicy] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setStatus(await api.ebayStatus());
    } catch (err) {
      setMessage((err as api.ApiError).message);
    }
    try {
      const data = await api.ebayLinks(db);
      setLinks(data.links ?? {});
    } catch {
      setLinks({});
    }
  }, [db]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    setMessage('');
    try {
      await action();
      setMessage(success);
      await refresh();
    } catch (err) {
      setMessage((err as api.ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  const itemOptions = useMemo(
    () => Object.entries(items).sort((a, b) => a[1].name.localeCompare(b[1].name)),
    [items],
  );
  const chosen = selected ? items[selected] : null;

  return (
    <div className="page">
      <div className="toolbar">
        <select value={db} onChange={(event) => setDb(event.target.value)}>
          {databases.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <button type="button" className="ghost" onClick={() => void refresh()}>
          Refresh
        </button>
      </div>

      {message && <p className="notice">{message}</p>}

      <section className="card">
        <h2>Seller account</h2>
        {!status ? (
          <p className="muted">Checking…</p>
        ) : !status.configured ? (
          <p className="warn">
            eBay app credentials are not configured on the backend. Set
            EbayClientId, EbayClientSecret and EbayRedirectUri when deploying the
            SAM stack.
          </p>
        ) : status.connected ? (
          <>
            <p>
              Connected to <strong>{status.env}</strong> ({status.marketplace_id}) since{' '}
              {status.connected_at}.
            </p>
            <button
              type="button"
              className="ghost danger"
              disabled={busy}
              onClick={() => void run(api.ebayDisconnect, 'Disconnected from eBay.')}
            >
              Disconnect
            </button>
          </>
        ) : (
          <>
            <p className="muted">
              Not connected. Authorize the seller account to publish listings.
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void (async () => {
                  try {
                    const { url } = await api.ebayAuthorizeUrl();
                    window.open(url, '_blank', 'noopener');
                  } catch (err) {
                    setMessage((err as api.ApiError).message);
                  }
                })()
              }
            >
              1. Open eBay consent page
            </button>
            <label>
              2. Paste the URL eBay redirected you to
              <input
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="https://your-redirect/?code=v%5E1.1…"
              />
            </label>
            <button
              type="button"
              className="primary"
              disabled={busy || !code.trim()}
              onClick={() =>
                void run(async () => {
                  await api.ebayConnect(code.trim());
                  setCode('');
                }, 'eBay account connected.')
              }
            >
              3. Complete connection
            </button>
          </>
        )}
      </section>

      {status?.connected && (
        <section className="card">
          <h2>Publish an item</h2>
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const data = await api.ebayPolicies();
                setPolicies(data);
                setFulfillment(data.fulfillment[0]?.id ?? '');
                setPaymentPolicy(data.payment[0]?.id ?? '');
                setReturnPolicy(data.return[0]?.id ?? '');
                setLocation(data.locations[0]?.key ?? '');
              }, 'Loaded policies and locations.')
            }
          >
            Load policies &amp; locations
          </button>

          {policies && (
            <>
              <label>
                Item
                <select value={selected} onChange={(event) => setSelected(event.target.value)}>
                  <option value="">- choose an item -</option>
                  {itemOptions.map(([upc, entry]) => (
                    <option key={upc} value={upc}>
                      {entry.name} (${Number(entry.price).toFixed(2)},{' '}
                      {entry.infinite ? '∞' : entry.count} in stock)
                    </option>
                  ))}
                </select>
              </label>

              <label>
                eBay category id
                <input
                  value={categoryId}
                  onChange={(event) => setCategoryId(event.target.value)}
                  placeholder="e.g. 139973"
                />
              </label>

              <label>
                Condition
                <select value={condition} onChange={(event) => setCondition(event.target.value)}>
                  {CONDITIONS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Inventory location
                <select value={location} onChange={(event) => setLocation(event.target.value)}>
                  {policies.locations.map((entry) => (
                    <option key={entry.key} value={entry.key}>
                      {entry.name}
                    </option>
                  ))}
                </select>
              </label>
              {policies.locations.length === 0 && (
                <p className="warn small">
                  eBay needs at least one inventory location before it will publish.
                  Create one in Seller Hub, then load again.
                </p>
              )}

              <label>
                Shipping policy
                <select
                  value={fulfillment}
                  onChange={(event) => setFulfillment(event.target.value)}
                >
                  {policies.fulfillment.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Payment policy
                <select
                  value={paymentPolicy}
                  onChange={(event) => setPaymentPolicy(event.target.value)}
                >
                  {policies.payment.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Return policy
                <select
                  value={returnPolicy}
                  onChange={(event) => setReturnPolicy(event.target.value)}
                >
                  {policies.return.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
                </select>
              </label>

              <button
                type="button"
                className="primary"
                disabled={
                  busy ||
                  !chosen ||
                  !categoryId.trim() ||
                  !location ||
                  !fulfillment ||
                  !paymentPolicy ||
                  !returnPolicy
                }
                onClick={() =>
                  void run(
                    () =>
                      api.ebayPublish(db, selected, {
                        category_id: categoryId.trim(),
                        condition,
                        merchant_location_key: location,
                        policies: {
                          fulfillment,
                          payment: paymentPolicy,
                          return: returnPolicy,
                        },
                        publish: true,
                      }),
                    'Listing published.',
                  )
                }
              >
                Publish to eBay
              </button>
            </>
          )}
        </section>
      )}

      <section className="card">
        <h2>Linked listings</h2>
        <div className="table-wrap">
          <table className="items">
            <thead>
              <tr>
                <th>UPC</th>
                <th>SKU</th>
                <th>Status</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Last synced</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {Object.entries(links).map(([upc, link]) => (
                <tr key={upc}>
                  <td className="mono">{upc}</td>
                  <td className="mono small">{String(link.sku ?? '')}</td>
                  <td>{String(link.status ?? '')}</td>
                  <td className="num">{String(link.quantity ?? '')}</td>
                  <td className="num">${Number(link.price ?? 0).toFixed(2)}</td>
                  <td className="muted small">{String(link.synced_at ?? '')}</td>
                  <td>
                    <button
                      type="button"
                      className="ghost"
                      disabled={busy}
                      onClick={() =>
                        void run(() => api.ebaySync(db, upc), 'Stock pushed to eBay.')
                      }
                    >
                      Sync stock
                    </button>
                    <button
                      type="button"
                      className="ghost danger"
                      disabled={busy}
                      onClick={() =>
                        void run(() => api.ebayEnd(db, upc), 'Listing ended.')
                      }
                    >
                      End
                    </button>
                  </td>
                </tr>
              ))}
              {Object.keys(links).length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">
                    No items from this database are listed on eBay yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
